import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import type { InteractiveLessonSectionFragment } from './lesson-builder.graphql.generated';
import type { BookmarkedSectionOverviewFragment } from '../shared/bookmarks/bookmark.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export type CommonComponentProps = {
  lessonId: number;
};

export type CommonColumnProps = {
  columnsData?: {
    columnComponentId: string;
    columnId: string;
  };
};

export type CommonSettingsProps = {
  contentId: number;
  onCloseChildDrawer?: () => void;
};

export type CommonPrintProps = {
  isPrintPreview: boolean;
};

export const ACTIVITY_BUILDER_SETTINGS_WIDTH = 80;
export const ACTIVITY_BUILDER_DRAWER_WIDTH = 320;
export const ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT = 768;
export const ACTIVITY_BUILDER_DRAWER_BREAKPOINT = `@media (min-width: ${ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT}px)`;
export const ACTIVITY_BUILDER_SLIDE_BREAKPOINT = 'min-width: 1176px';

// z-index's
export const ACTIVITY_BUILDER_SETTINGS_DRAWER_Z_INDEX = 100;
export const ACTIVITY_BUILDER_HEADER_CONTAINER_Z_INDEX = 200;
export const ACTIVITY_BUILDER_DRAWER_OVERLAY_Z_INDEX = 102;
export const ACTIVITY_BUILDER_HEADER_TOOLBAR_Z_INDEX = 99;
export const ACTIVITY_BUILDER_HELP_Z_INDEX = 99;
export const ACTIVITY_BUILDER_CANVAS_Z_INDEX = 98;
export const ACTIVITY_BUILDER_IMAGE_CROP_ZOOM_Z_INDEX = 11;
export const ACTIVITY_BUILDER_DRAGGABLE_SURFACE_HANDLE_Z_INDEX = 10;
export const ACTIVITY_BUILDER_RIVE_BUTTONS_Z_INDEX = 11;

export type BaseSectionOverview = {
  id: string;
  sort: string;
  differentiation: number | null;
  checkPoint?: boolean;
  parentSectionId?: string | null;
  activityId?: string | null;
  subSectionsOverview?:
    | {
        id: string;
        sort: string;
        activityId?: string | null;
      }[]
    | null;
};

export type SectionOrSubSection =
  | InteractiveLessonSectionFragment
  | Pluck<InteractiveLessonSectionFragment, 'subSections'>;

export type SectionOrSubSectionOverview =
  | Pluck<LessonActivitiesFragment, 'sectionsOverview'>
  | Pluck<LessonActivitiesFragment, 'sectionsOverview.subSectionsOverview'>
  | BookmarkedSectionOverviewFragment;

export const ACTIVITY_BUILDER_TOOLBAR_ID = 'activity-builder-toolbar';
export const ACTIVITY_BUILDER_DRAWER_ID = 'activity-builder-drawer';
export const ACTIVITY_BUILDER_GLOBAL_MENU_ID = 'activity-builder-global-menu';
export const ACTIVITY_BUILDER_COMPONENT_MENU =
  'activity-builder-component-menu';
export const ACTIVITY_BUILDER_COMPONENT_MENU_EXTRA =
  'activity-builder-component-menu-extra';
export const ACTIVITY_BUILDER_GLOBAL_ADD = 'activity-builder-global-add';
export const ACTIVITY_BUILDER_GLOBAL_ADD_TOP =
  'activity-builder-global-add-top';
export const ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE = 'lb-click-ignore';

export const ACTIVITY_BUILDER_CANVAS_ID = 'lb-canvas';

export const ACTIVITY_BUILDER_A4_SHORT_SIDE = 793; // 21cm
export const ACTIVITY_BUILDER_A4_LONG_SIDE = 1123; // 29.7cm
export const ACTIVITY_BUILDER_HEADER_HEIGHT = 64;
export const ACTIVITY_BUILDER_FOOTER_HEIGHT = 56;
export const ACTIVITY_BUILDER_ACTIVITY_HEIGHT = 48;
export const SLIDE_HEIGHT_MINUS_HEADER_FOOTER =
  ACTIVITY_BUILDER_A4_SHORT_SIDE -
  ACTIVITY_BUILDER_HEADER_HEIGHT -
  ACTIVITY_BUILDER_FOOTER_HEIGHT -
  ACTIVITY_BUILDER_ACTIVITY_HEIGHT;
export const ACTIVITY_BUILDER_SLIDE_RATIO =
  ACTIVITY_BUILDER_A4_SHORT_SIDE / ACTIVITY_BUILDER_A4_LONG_SIDE;

export const ACTIVITY_BUILDER_DRAWER_HISTORY = 'history';
export const ACTIVITY_BUILDER_DRAWER_TEACHING_NOTES = 'teaching-notes';
export const ACTIVITY_BUILDER_DRAWER_ADD_COMPONENT = 'add-component';
export const ACTIVITY_BUILDER_DRAWER_EMPTY_EDIT = 'empty-edit';
export const ACTIVITY_BUILDER_DRAWER_GLOBAL_SETTINGS = 'global-settings';
export const ACTIVITY_BUILDER_DRAWER_EMPTY_COLUMN_ADD = 'column-add';
export const ACTIVITY_BUILDER_DRAWER_STYLES = 'styles';
export const ACTIVITY_BUILDER_DRAWER_ACTIVITY = 'activity';

export const ACTIVITY_BUILDER_MORE_MENU_CONTAINER = 'more-menu-container';
